import { useEffect } from "react";
import axios from "axios";
import { clearSession, getRefreshToken, refreshSession } from "../services/auth";
import { apiBaseUrl } from "../helpers/apiBase";

/** Эндпоинты входа сами выдают токены — продлевать сессию для них бессмысленно. */
const AUTH_ENDPOINTS = ["/api/auth/", "add-user.php", "login"];

function isAuthEndpoint(url?: string): boolean {
  if (!url) {
    return false;
  }
  return AUTH_ENDPOINTS.some((path) => url.includes(path));
}

const useAxiosSetup = () => {
  useEffect(() => {
    axios.defaults.baseURL = apiBaseUrl();

    // Интерцептор запросов
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        // Добавляем токен авторизации
        const token = localStorage.getItem("auth_token");
        if (token) {
          config.headers.Authorization = token;
        }

        // Добавляем X-App-Token из localStorage
        const appToken = localStorage.getItem("app_token");
        if (appToken) {
          config.headers["X-App-Token"] = appToken;
        }

        // Добавляем CSRF-токен для небезопасных методов
        if (
          ["POST", "PUT", "DELETE", "PATCH"].includes(
            config.method?.toUpperCase() || ""
          )
        ) {
          const csrfToken = localStorage.getItem("csrf_token");
          if (csrfToken) {
            config.headers["X-CSRF-Token"] = csrfToken;
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Интерцептор ответов
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        // Обновляем CSRF-токен, если он пришел в заголовках
        const csrfToken = response.headers["x-csrf-token"];
        if (csrfToken) {
          localStorage.setItem("csrf_token", csrfToken);
        }

        // Обновляем X-App-Token, если он пришел в заголовках
        const appToken = response.headers["x-app-token"];
        if (appToken) {
          localStorage.setItem("app_token", appToken);
        }

        return response;
      },
      (error) => {
        // Обработка ошибок авторизации
        if (
          error.response &&
          (error.response.status === 401 || error.response.status === 403)
        ) {
          // Проверяем, не связана ли ошибка с необходимостью CSRF-токена
          if (error.response.data?.message?.includes("CSRF")) {
            // Можно попробовать получить новый CSRF-токен и повторить запрос
            return axios
              .get("/api/generate_csrf.php")
              .then(() => {
                // Повторяем оригинальный запрос
                return axios(error.config);
              })
              .catch(() => {
                // Если не удалось получить CSRF-токен, разлогиниваем
                localStorage.removeItem("auth_token");
                localStorage.removeItem("csrf_token");
                // window.location.href = "/";
                return Promise.reject(error);
              });
          }

          // Веб-сессия: JWT живёт час, поэтому истёкший токен — обычное дело.
          // Меняем refresh-токен на новый JWT и повторяем запрос ровно один раз.
          // В мини-аппе refresh-токена нет, и поведение остаётся прежним.
          const originalRequest = error.config || {};
          if (
            error.response.status === 401 &&
            !isAuthEndpoint(originalRequest.url) &&
            !originalRequest._retriedAfterRefresh &&
            getRefreshToken()
          ) {
            originalRequest._retriedAfterRefresh = true;
            return refreshSession().then((renewed) => {
              if (!renewed) {
                return Promise.reject(error);
              }
              // Заголовок берётся из localStorage интерцептором запроса,
              // так что повтор уйдёт уже с новым токеном.
              delete originalRequest.headers?.Authorization;
              return axios(originalRequest);
            });
          }

          // Сессию стираем только по 401: это ответ «токен не принят», и
          // обмен refresh-токена выше уже не помог. 403 — совсем другое:
          // так отвечают лимиты и правила бизнес-логики (например,
          // telegram_required при выводе, «Access denied» от middleware
          // на один конкретный запрос). Раньше любой 403 разлогинивал
          // человека прямо посреди работы: нажал «Вывести» — и оказался
          // на экране входа.
          if (
            error.response.status === 401 &&
            !isAuthEndpoint(error.config.url)
          ) {
            clearSession();
            // window.location.href = "/";
          }
        }

        // Обработка ошибок рейт-лимита
        if (error.response && error.response.status === 429) {
          // Можно показать уведомление о превышении лимита запросов
          console.error("Rate limit exceeded. Please try again later.");
        }

        return Promise.reject(error);
      }
    );

    return () => {
      // Удаляем интерцепторы при размонтировании
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);
};

export default useAxiosSetup;
